import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Table,
  Input,
  Breadcrumb,
  Tag,
  Modal,
  Select,
  message,
  Button,
  DatePicker,
  Space,
  Grid,
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Avatar,
  Tooltip,
  Dropdown,
  Badge,
  Divider,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  RightOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  CustomerServiceOutlined,
  UserOutlined,
  PhoneOutlined,
  MessageOutlined,
  CalendarOutlined,
  FilterOutlined,
  ReloadOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { io } from 'socket.io-client';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AuthContext } from '../../common/constants';
import { demandeAssistancesService } from '../../common/services';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const socket = io('https://api.tigoon-connect.com/api/v1');

// Configuration des statuts avec couleurs et icônes
const STATUS_CONFIG = {
  en_attente: {
    label: 'En attente',
    color: 'warning',
    bgColor: '#fffbe6',
    textColor: '#faad14',
    icon: <ClockCircleOutlined />,
  },
  en_cours: {
    label: 'En cours',
    color: 'processing',
    bgColor: '#e6f7ff',
    textColor: '#1890ff',
    icon: <SyncOutlined spin />,
  },
  resolue: {
    label: 'Résolue',
    color: 'success',
    bgColor: '#f6ffed',
    textColor: '#52c41a',
    icon: <CheckCircleOutlined />,
  },
  non_resolue: {
    label: 'Non résolue',
    color: 'error',
    bgColor: '#fff2f0',
    textColor: '#ff4d4f',
    icon: <CloseCircleOutlined />,
  },
};

// Configuration des motifs avec émojis
const MOTIF_CONFIG = {
  panne: { emoji: '🔧', color: '#fa8c16' },
  bonus: { emoji: '🎁', color: '#722ed1' },
  'une demande.': { emoji: '📝', color: '#1890ff' },
  'débité sans être crédité': { emoji: '💳', color: '#f5222d' },
  autre: { emoji: '❓', color: '#595959' },
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  let local = phone.replace('+225', '').trim();
  return '+225 ' + local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

const formatDate = (date) => {
  if (!date) return '-';

  // Essayer plusieurs formats de date
  const formats = [
    'YYYY-MM-DDTHH:mm:ss.SSSZ', // ISO
    'YYYY-MM-DD HH:mm:ss',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY',
  ];

  let parsed = dayjs(date);

  // Si le parsing standard échoue, essayer les formats personnalisés
  if (!parsed.isValid()) {
    for (const fmt of formats) {
      parsed = dayjs(date, fmt);
      if (parsed.isValid()) break;
    }
  }

  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }

  // Si c'est déjà une chaîne formatée, la retourner telle quelle
  if (typeof date === 'string') {
    return date;
  }

  return '-';
};

// Générer une couleur basée sur le nom
const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#722ed1'];
  const index = (name || '').charCodeAt(0) % colors.length;
  return colors[index];
};

const DemandesAssistances = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // Statistiques
  const [stats, setStats] = useState({
    total: 0,
    en_attente: 0,
    en_cours: 0,
    resolue: 0,
    non_resolue: 0,
  });

  // Filtres / recherche
  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [statutFilter, setStatutFilter] = useState(undefined);

  // Pagination / tri
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-createdAt');

  // Modal traitement
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedStatut, setSelectedStatut] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Modal détails
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [detailsDemande, setDetailsDemande] = useState(null);

  const { currentUser } = useContext(AuthContext);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;

  // Calculer les stats à partir des items
  const calculateStats = useCallback((data) => {
    const newStats = {
      total: data.length,
      en_attente: data.filter(d => d.statut === 'en_attente').length,
      en_cours: data.filter(d => d.statut === 'en_cours').length,
      resolue: data.filter(d => d.statut === 'resolue').length,
      non_resolue: data.filter(d => d.statut === 'non_resolue').length,
    };
    setStats(newStats);
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const cleanedQ = q ? q.replace(/\s+/g, '') : '';
      const params = {
        page, limit, sort,
        q: cleanedQ || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
        statut: statutFilter || undefined,
      };
      const data = await demandeAssistancesService.getList(params);
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
        calculateStats(data);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
        // Fetch all for stats if paginated
        if (!statutFilter) {
          const allData = await demandeAssistancesService.getList({ ...params, limit: 10000 });
          calculateStats(Array.isArray(allData) ? allData : (allData.items || []));
        }
      }
    } catch (e) {
      console.error(e);
      message.error("Erreur lors du chargement des demandes");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, q, dateRange, statutFilter, calculateStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // WebSocket -> rechargement live
  useEffect(() => {
    const onNew = () => fetchList();
    const onUpdated = () => fetchList();

    socket.on('nouvelle-demande', onNew);
    socket.on('demande_assistance_updated', onUpdated);

    return () => {
      socket.off('nouvelle-demande', onNew);
      socket.off('demande_assistance_updated', onUpdated);
    };
  }, [fetchList]);

  // EXPORTS
  const fetchAllForExport = async () => {
    const cleanedQ = q ? q.replace(/\s+/g, '') : '';
    const params = {
      page: 1, limit: 5000, sort,
      q: cleanedQ || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end: dateRange?.[1]?.format('YYYY-MM-DD'),
      statut: statutFilter || undefined,
    };
    const data = await demandeAssistancesService.getList(params);
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportExcel = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(d => ({
        Nom: d.utilisateur?.prenom || '',
        Prénom: d.utilisateur?.nom || '',
        Téléphone: formatPhoneNumber(d.utilisateur?.telephone || ''),
        Motif: d.motif || '',
        Message: d.message || '',
        Commentaire: d.commentaire || '',
        Statut: STATUS_CONFIG[d.statut]?.label || d.statut || '',
        'Traité par': d.adminTraiteur ? `${d.adminTraiteur.prenom} ${d.adminTraiteur.nom}` : '—',
        'Créée le': formatDate(d.createdAt),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Demandes_assistance');
      XLSX.writeFile(wb, 'demandes_assistance.xlsx');
      message.success({ content: 'Export Excel réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export Excel: une erreur est survenue.', key: 'export' });
    }
  };

  const exportCSV = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(d => ({
        Nom: d.utilisateur?.prenom || '',
        Prenom: d.utilisateur?.nom || '',
        Telephone: formatPhoneNumber(d.utilisateur?.telephone || ''),
        Motif: d.motif || '',
        Message: d.message || '',
        Commentaire: d.commentaire || '',
        Statut: STATUS_CONFIG[d.statut]?.label || d.statut || '',
        TraitePar: d.adminTraiteur ? `${d.adminTraiteur.prenom} ${d.adminTraiteur.nom}` : '—',
        CreeLe: formatDate(d.createdAt),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'demandes_assistance.csv');
      message.success({ content: 'Export CSV réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export CSV: une erreur est survenue.', key: 'export' });
    }
  };

  const exportPDF = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const doc = new jsPDF();
      doc.text("Demandes d'assistance", 14, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Nom', 'Prénom', 'Téléphone', 'Motif', 'Statut', 'Traité par', 'Date']],
        body: all.map(d => [
          d.utilisateur?.prenom || '',
          d.utilisateur?.nom || '',
          formatPhoneNumber(d.utilisateur?.telephone || ''),
          d.motif || '',
          STATUS_CONFIG[d.statut]?.label || d.statut || '',
          d.adminTraiteur ? `${d.adminTraiteur.prenom} ${d.adminTraiteur.nom}` : '—',
          formatDate(d.createdAt),
        ]),
      });
      doc.save('demandes_assistance.pdf');
      message.success({ content: 'Export PDF réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export PDF: une erreur est survenue.', key: 'export' });
    }
  };

  const exportMenuItems = [
    { key: 'excel', icon: <FileExcelOutlined style={{ color: '#52c41a' }} />, label: 'Excel (.xlsx)', onClick: exportExcel },
    { key: 'csv', icon: <DownloadOutlined style={{ color: '#1890ff' }} />, label: 'CSV', onClick: exportCSV },
    { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />, label: 'PDF', onClick: exportPDF },
  ];

  const openModal = (demande) => {
    setSelectedDemande(demande);
    setSelectedStatut(demande.statut || 'en_attente');
    setCommentaire(demande.commentaire || '');
    setModalVisible(true);
  };

  const openDetailsModal = (demande) => {
    setDetailsDemande(demande);
    setDetailsModalVisible(true);
  };

  const handleTraitement = async () => {
    if (!selectedStatut) return message.warning("Veuillez sélectionner un statut");
    if (selectedStatut === 'non_resolue' && !commentaire.trim()) {
      return message.warning("Veuillez fournir un commentaire pour les demandes non résolues");
    }
    try {
      setIsUpdating(true);
      await demandeAssistancesService.traiterDemandeAssistance(selectedDemande.id, {
        statut: selectedStatut,
        adminId: currentUser?._id,
        commentaireResolution: commentaire,
      });
      message.success("Demande mise à jour avec succès");
      setModalVisible(false);
      fetchList();
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la mise à jour");
    } finally {
      setIsUpdating(false);
    }
  };

  const columns = [
    {
      title: 'Demandeur',
      key: 'demandeur',
      width: 220,
      render: (_, record) => {
        const nom = record.utilisateur?.prenom || '';
        const prenom = record.utilisateur?.nom || '';
        const fullName = `${nom} ${prenom}`.trim() || 'Inconnu';
        const phone = record.utilisateur?.telephone;
        const initials = `${nom.charAt(0)}${prenom.charAt(0)}`.toUpperCase() || '?';

        return (
          <Space>
            <Avatar
              style={{ backgroundColor: getAvatarColor(nom) }}
              size={40}
            >
              {initials}
            </Avatar>
            <div>
              <Text strong style={{ display: 'block' }}>{fullName}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {formatPhoneNumber(phone)}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Motif',
      dataIndex: 'motif',
      key: 'motif',
      width: 160,
      ellipsis: true,
      render: (motif) => {
        const config = MOTIF_CONFIG[motif] || MOTIF_CONFIG.autre;
        return (
          <Tooltip title={motif}>
            <Tag
              style={{
                backgroundColor: `${config.color}15`,
                borderColor: config.color,
                color: config.color,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              <span style={{ marginRight: 4 }}>{config.emoji}</span>
              {motif || '-'}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      width: 200,
      render: (msg) => (
        <Tooltip title={msg}>
          <Text style={{ fontSize: 13 }}>{msg || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Commentaire',
      dataIndex: 'commentaire',
      key: 'commentaire',
      ellipsis: true,
      width: 180,
      responsive: ['lg'],
      render: (com) => (
        <Tooltip title={com}>
          <Text type="secondary" style={{ fontSize: 13 }}>{com || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      sorter: true,
      render: (d) => {
        if (!d) return <Text type="secondary">-</Text>;

        // Essayer de parser avec plusieurs formats
        const formats = ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY'];
        let parsed = dayjs(d);

        if (!parsed.isValid()) {
          for (const fmt of formats) {
            parsed = dayjs(d, fmt);
            if (parsed.isValid()) break;
          }
        }

        // Si toujours invalide et c'est une chaîne formatée
        if (!parsed.isValid() && typeof d === 'string') {
          const parts = d.split(' ');
          return (
            <Space direction="vertical" size={0}>
              <Text style={{ fontSize: 12 }}>{parts[0] || d}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{parts[1] || ''}</Text>
            </Space>
          );
        }

        return (
          <Tooltip title={formatDate(d)}>
            <Space direction="vertical" size={0}>
              <Text style={{ fontSize: 12 }}>{parsed.isValid() ? parsed.format('DD/MM/YYYY') : d}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{parsed.isValid() ? parsed.format('HH:mm') : ''}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Statut',
      dataIndex: 'statut',
      key: 'statut',
      width: 120,
      sorter: true,
      filters: [
        { text: 'En attente', value: 'en_attente' },
        { text: 'En cours', value: 'en_cours' },
        { text: 'Résolue', value: 'resolue' },
        { text: 'Non résolue', value: 'non_resolue' },
      ],
      render: (statut) => {
        const config = STATUS_CONFIG[statut] || { label: '—', color: 'default', icon: null };
        return (
          <Tag
            color={config.color}
            icon={config.icon}
            style={{ fontWeight: 600, fontSize: 12 }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: 'Traité par',
      key: 'adminTraiteur',
      width: 150,
      responsive: ['md'],
      render: (_, record) => {
        const adm = record.adminTraiteur;
        if (!adm) return <Text type="secondary">-</Text>;
        const fullName = `${adm.prenom || ''} ${adm.nom || ''}`.trim();
        return (
          <Space>
            <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
              {fullName.charAt(0)}
            </Avatar>
            <Text style={{ fontSize: 12 }}>{fullName}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const statut = record?.statut;
        const adminEnCoursId = record?.adminTraiteur?.id ?? record?.adminTraiteur?._id ?? null;
        const currentId = currentUser?._id ?? currentUser?.id ?? null;
        const sameAdmin = adminEnCoursId && currentId && String(adminEnCoursId) === String(currentId);
        const canSeeTraiter =
          statut === 'en_attente' || statut === 'non_resolue' || (statut === 'en_cours' && sameAdmin);

        const actionItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Voir détails',
            onClick: () => openDetailsModal(record),
          },
        ];

        if (canSeeTraiter && statut !== 'resolue') {
          actionItems.push({
            key: 'traiter',
            icon: <EditOutlined />,
            label: 'Traiter',
            onClick: () => openModal(record),
          });
        }

        return (
          <Dropdown
            menu={{ items: actionItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
          </Dropdown>
        );
      },
    },
  ];

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    if (filters?.statut && filters.statut.length) {
      setStatutFilter(filters.statut[0]);
      setPage(1);
    } else if (statutFilter && !filters?.statut?.length) {
      setStatutFilter(undefined);
      setPage(1);
    }

    if (sorter?.field || sorter?.columnKey) {
      const key = sorter.columnKey || sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      const allowed = [
        'createdAt', 'statut', 'motif',
        'utilisateur.nom', 'utilisateur.prenom', 'utilisateur.telephone',
        'adminTraiteur.nom', 'adminTraiteur.prenom'
      ];
      if (allowed.includes(key)) setSort(`${dir}${key}`);
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: 'Assistance' },
          { title: 'Demandes' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total</span>}
              value={stats.total}
              valueStyle={{ color: '#fff', fontSize: isMobile ? 20 : 24 }}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card
            size="small"
            style={{ background: STATUS_CONFIG.en_attente.bgColor, borderColor: STATUS_CONFIG.en_attente.textColor }}
          >
            <Statistic
              title={<span style={{ color: STATUS_CONFIG.en_attente.textColor }}>En attente</span>}
              value={stats.en_attente}
              valueStyle={{ color: STATUS_CONFIG.en_attente.textColor, fontSize: isMobile ? 20 : 24 }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card
            size="small"
            style={{ background: STATUS_CONFIG.en_cours.bgColor, borderColor: STATUS_CONFIG.en_cours.textColor }}
          >
            <Statistic
              title={<span style={{ color: STATUS_CONFIG.en_cours.textColor }}>En cours</span>}
              value={stats.en_cours}
              valueStyle={{ color: STATUS_CONFIG.en_cours.textColor, fontSize: isMobile ? 20 : 24 }}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={5}>
          <Card
            size="small"
            style={{ background: STATUS_CONFIG.resolue.bgColor, borderColor: STATUS_CONFIG.resolue.textColor }}
          >
            <Statistic
              title={<span style={{ color: STATUS_CONFIG.resolue.textColor }}>Résolues</span>}
              value={stats.resolue}
              valueStyle={{ color: STATUS_CONFIG.resolue.textColor, fontSize: isMobile ? 20 : 24 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Card
            size="small"
            style={{ background: STATUS_CONFIG.non_resolue.bgColor, borderColor: STATUS_CONFIG.non_resolue.textColor }}
          >
            <Statistic
              title={<span style={{ color: STATUS_CONFIG.non_resolue.textColor }}>Non résolues</span>}
              value={stats.non_resolue}
              valueStyle={{ color: STATUS_CONFIG.non_resolue.textColor, fontSize: isMobile ? 20 : 24 }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et tableau */}
      <Card>
        {/* Filtres */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher par nom, téléphone, motif..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              allowClear
              size={isMobile ? 'middle' : 'large'}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              onChange={(r) => { setDateRange(r); setPage(1); }}
              placeholder={['Date début', 'Date fin']}
              format="DD/MM/YYYY"
              allowClear
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              allowClear
              placeholder="Filtrer par statut"
              value={statutFilter}
              onChange={(v) => { setStatutFilter(v); setPage(1); }}
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              suffixIcon={<FilterOutlined />}
            >
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <Select.Option key={key} value={key}>
                  <Space>
                    {config.icon}
                    {config.label}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={5}>
            <Dropdown
              menu={{ items: exportMenuItems }}
              trigger={['click']}
            >
              <Button
                icon={<DownloadOutlined />}
                size={isMobile ? 'middle' : 'large'}
                style={{ width: '100%' }}
              >
                Exporter <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
              </Button>
            </Dropdown>
          </Col>
        </Row>

        {/* Résultats */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            {total} demande{total > 1 ? 's' : ''} trouvée{total > 1 ? 's' : ''}
          </Text>
        </div>

        {/* Tableau */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          onChange={onTableChange}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
          }}
          rowClassName={(_, index) =>
            index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
          }
        />
      </Card>

      {/* Modal traitement */}
      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: '#1890ff' }} />
            <span>Traiter la demande</span>
          </Space>
        }
        open={isModalVisible}
        onOk={handleTraitement}
        onCancel={() => setModalVisible(false)}
        okText="Mettre à jour"
        cancelText="Annuler"
        centered
        width={isMobile ? '95%' : 520}
        confirmLoading={isUpdating}
        destroyOnClose
      >
        {selectedDemande && (
          <div>
            {/* Info demande */}
            <Card
              size="small"
              style={{ marginBottom: 16, backgroundColor: '#fafafa' }}
            >
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Text type="secondary">Demandeur :</Text>
                  <br />
                  <Text strong>
                    {`${selectedDemande.utilisateur?.prenom || ''} ${selectedDemande.utilisateur?.nom || ''}`.trim()}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Téléphone :</Text>
                  <br />
                  <Text>{formatPhoneNumber(selectedDemande.utilisateur?.telephone)}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Motif :</Text>
                  <br />
                  <Tag color="blue">{selectedDemande.motif}</Tag>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Date :</Text>
                  <br />
                  <Text>{formatDate(selectedDemande.createdAt)}</Text>
                </Col>
                <Col span={24}>
                  <Text type="secondary">Message :</Text>
                  <br />
                  <Text>{selectedDemande.message || '-'}</Text>
                </Col>
              </Row>
            </Card>

            <Divider style={{ margin: '16px 0' }} />

            {/* Formulaire */}
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Nouveau statut :
                </Text>
                <Select
                  value={selectedStatut}
                  onChange={setSelectedStatut}
                  style={{ width: '100%' }}
                  size="large"
                >
                  {Object.entries(STATUS_CONFIG)
                    .filter(([key]) => key !== 'en_attente')
                    .map(([key, config]) => (
                      <Select.Option key={key} value={key}>
                        <Space>
                          <span style={{ color: config.textColor }}>{config.icon}</span>
                          {config.label}
                        </Space>
                      </Select.Option>
                    ))}
                </Select>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Commentaire {selectedStatut === 'non_resolue' && <Text type="danger">*</Text>} :
                </Text>
                <Input.TextArea
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  rows={4}
                  placeholder="Entrez un commentaire (obligatoire si non résolue)"
                  showCount
                  maxLength={500}
                />
              </div>
            </Space>
          </div>
        )}
      </Modal>

      {/* Modal détails */}
      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <span>Détails de la demande</span>
          </Space>
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Fermer
          </Button>
        ]}
        centered
        width={isMobile ? '95%' : 600}
      >
        {detailsDemande && (
          <div>
            {/* Statut actuel */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Tag
                color={STATUS_CONFIG[detailsDemande.statut]?.color}
                icon={STATUS_CONFIG[detailsDemande.statut]?.icon}
                style={{ fontSize: 16, padding: '8px 16px' }}
              >
                {STATUS_CONFIG[detailsDemande.statut]?.label}
              </Tag>
            </div>

            {/* Infos demandeur */}
            <Card size="small" title="Demandeur" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary">Nom complet :</Text>
                  <br />
                  <Text strong>
                    {`${detailsDemande.utilisateur?.prenom || ''} ${detailsDemande.utilisateur?.nom || ''}`.trim() || '-'}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Téléphone :</Text>
                  <br />
                  <Text>{formatPhoneNumber(detailsDemande.utilisateur?.telephone) || '-'}</Text>
                </Col>
              </Row>
            </Card>

            {/* Infos demande */}
            <Card size="small" title="Demande" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary">Motif :</Text>
                  <br />
                  <Tag color="blue">{detailsDemande.motif || '-'}</Tag>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Date :</Text>
                  <br />
                  <Text>{formatDate(detailsDemande.createdAt)}</Text>
                </Col>
                <Col span={24}>
                  <Text type="secondary">Message :</Text>
                  <br />
                  <Text>{detailsDemande.message || '-'}</Text>
                </Col>
              </Row>
            </Card>

            {/* Traitement */}
            {detailsDemande.adminTraiteur && (
              <Card size="small" title="Traitement">
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <Text type="secondary">Traité par :</Text>
                    <br />
                    <Text strong>
                      {`${detailsDemande.adminTraiteur.prenom || ''} ${detailsDemande.adminTraiteur.nom || ''}`.trim()}
                    </Text>
                  </Col>
                  <Col span={24}>
                    <Text type="secondary">Commentaire :</Text>
                    <br />
                    <Text>{detailsDemande.commentaire || '-'}</Text>
                  </Col>
                </Row>
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* Styles */}
      <style>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-light:hover td,
        .table-row-dark:hover td {
          background-color: #e6f7ff !important;
        }
      `}</style>
    </>
  );
};

export default DemandesAssistances;
